import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Loading from '../../components/Loading';

function DoorsStockStatus() {
  const [doors, setDoors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDoor, setSelectedDoor] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStock, setEditingStock] = useState({});
  const [stockValues, setStockValues] = useState({});
  const [editingImage, setEditingImage] = useState(null);
  const [newImage, setNewImage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  useEffect(() => {
    fetchDoors();
  }, []);
  const fetchDoors = () => {
    setIsLoading(true);
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/doors`)
      .then(response => {
        setDoors(response.data);
        setIsLoading(false);
      })
      .catch(error => {
        Swal.fire('Error', 'Failed to fetch doors', 'error');
        setError(error);
        setIsLoading(false);
      });
  };
  const checkDoorTransactions = async (doorId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/doors/${doorId}/transactions`);
      return !response.data.hasTransactions;
    } catch (error) {
      console.error('Error checking transactions:', error);
      return false;
    }
  };

  const handleEditStock = (door) => {
    setEditingStock({ ...editingStock, [door._id]: true });
    setStockValues({
      ...stockValues,
      [door._id]: {
        stock: door.stock,
        damaged_stock: door.damaged_stock
      }
    });
  };

  const handleSaveStock = async (door) => {
    try {
      const values = stockValues[door._id];
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/doors/${door._id}/stock`, values);
      Swal.fire('Success', 'Stock updated successfully', 'success');
      setEditingStock({ ...editingStock, [door._id]: false });
      fetchDoors();
    } catch (error) {
      Swal.fire('Error', 'Failed to update stock', 'error');
    }
  };

  const handleCancelStock = (door) => {
    setEditingStock({ ...editingStock, [door._id]: false });
    setStockValues({ ...stockValues, [door._id]: undefined });
  };

  const handleStockChange = (doorId, field, value) => {
    setStockValues({
      ...stockValues,
      [doorId]: {
        ...stockValues[doorId],
        [field]: parseInt(value) || 0
      }
    });
  };

  const handleEditImage = (door) => {
    setEditingImage(door._id);
  };

  const handleImageChange = (e) => {
    setNewImage(e.target.files[0]);
  };

  const handleSaveImage = async (door) => {
    if (!newImage) {
      Swal.fire('Error', 'Please select an image', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('image', newImage);

    try {
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/doors/${door._id}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      Swal.fire('Success', 'Image updated successfully', 'success');
      setEditingImage(null);
      setNewImage(null);
      fetchDoors();
    } catch (error) {
      Swal.fire('Error', 'Failed to update image', 'error');
    }
  };
  const handleCancelImage = () => {
    setEditingImage(null);
    setNewImage(null);
  };

  const handleDeleteDoor = async (door) => {
    const canDelete = await checkDoorTransactions(door._id);
    
    if (!canDelete) {
      Swal.fire({
        title: 'Cannot Delete Door',
        text: 'This door has inward or outward transactions and cannot be deleted.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete "${door.designName}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/doors/${door._id}`);
        Swal.fire('Deleted!', 'Door has been deleted successfully.', 'success');
        fetchDoors();
      } catch (error) {
        Swal.fire('Error', 'Failed to delete door', 'error');
      }
    }
  };
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };
  const showImageModal = (door) => {
    setSelectedDoor(door);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDoor(null);
  };  const filteredDoors = doors.filter(door => {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    const lowerCaseDoorName = door.designName?.toLowerCase() || '';
    const lowerCaseDoorType = door.doorType?.parameterName?.toLowerCase() || '';
    return lowerCaseDoorName.includes(lowerCaseSearchTerm) ||
      lowerCaseDoorType.includes(lowerCaseSearchTerm);
  });

  // Calculate pagination
  const totalDoors = filteredDoors.length;
  const totalPagesCount = Math.ceil(totalDoors / itemsPerPage);
  const indexOfLastDoor = currentPage * itemsPerPage;
  const indexOfFirstDoor = indexOfLastDoor - itemsPerPage;
  const currentDoors = filteredDoors.slice(indexOfFirstDoor, indexOfLastDoor);

  // Update total pages when filtered doors change
  useEffect(() => {
    const newTotalPages = Math.ceil(filteredDoors.length / itemsPerPage);
    setTotalPages(newTotalPages);
    
    // If current page is greater than total pages, reset to page 1
    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(1);
    }
  }, [filteredDoors.length, itemsPerPage, currentPage]);

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);
    
    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };const Modal = ({ isOpen, onClose, door }) => {
    if (!isOpen || !door) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full">
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-xl font-bold text-gray-900">{door.designName}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>
          
          <div className="p-6">
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <img 
                src={`${process.env.REACT_APP_API_BASE_URL}/doors/image/${door._id}`}
                alt={door.designName}
                className="w-full h-80 object-cover"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik05MCA2MEg2MEMxNi44NiA2MCA2MCA2NCA2MCA3MEw2MCA5MEgxNDBWNzBDMTQwIDY0IDE0NCA2MCAxMzAgNjBIOTBaIiBmaWxsPSIjNkI3MjgwIi8+CjxwYXRoIGQ9Ik02MCA5MEgxNDBWMTMwSDYwVjkwWiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNNjAgMTMwSDE0MFYxNDBDMTQwIDE0NiAxMzYgMTUwIDEzMCAxNTBIOTBINzBDNjQgMTUwIDYwIDE0NiA2MCAxNDBWMTMwWiIgZmlsbD0iIzZCNzI4MCIvPgo8L3N2Zz4K';
                }}
              />
            </div>
          </div>
          
          <div className="flex justify-end p-6 border-t">
            <button
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-6 rounded-lg transition duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };
  const EditStockButton = ({ door, onEdit }) => {
    const [canEdit, setCanEdit] = useState(false);
    const [checking, setChecking] = useState(false);

    const handleClick = async () => {
      setChecking(true);
      const canEditStock = await checkDoorTransactions(door._id);
      setCanEdit(canEditStock);
      setChecking(false);
      
      if (canEditStock) {
        onEdit(door);
      } else {
        Swal.fire({
          title: 'Cannot Edit Stock',
          text: 'This door has inward or outward transactions and stock cannot be edited.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
      }
    };

    return (
      <button
        onClick={handleClick}
        disabled={checking}
        className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white py-2 px-3 rounded-lg transition duration-200 flex items-center"
      >
        {checking ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Checking...
          </>
        ) : (
          'Edit Stock'
        )}
      </button>
    );
  };

  const DeleteDoorButton = ({ door, onDelete }) => {
    const [canDelete, setCanDelete] = useState(false);
    const [checking, setChecking] = useState(false);

    const handleClick = async () => {
      setChecking(true);
      const canDeleteDoor = await checkDoorTransactions(door._id);
      setCanDelete(canDeleteDoor);
      setChecking(false);
      
      if (canDeleteDoor) {
        onDelete(door);
      } else {
        Swal.fire({
          title: 'Cannot Delete Door',
          text: 'This door has inward or outward transactions and cannot be deleted.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
      }
    };

    return (
      <button
        onClick={handleClick}
        disabled={checking}
        className="bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white py-2 px-3 rounded-lg transition duration-200 flex items-center"
      >
        {checking ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Checking...
          </>
        ) : (
          'Delete'
        )}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Doors Stock Status</h1>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="max-w-md">
              <input 
                type="text" 
                value={searchTerm} 
                onChange={handleSearch} 
                placeholder="Search by door name or type" 
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Items per page:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </div>

        {isLoading ? (
          <Loading />
        ) : (
          <>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                Error: {error.message}
              </div>
            )}            {/* Desktop Table View */}
            <div className="bg-white shadow-xl rounded-lg overflow-hidden mb-8">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-600">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Door ID</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Door Name</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Door Type</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Thickness</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Size</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Stock</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Damaged Stock</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Image</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>                <tbody className="bg-white divide-y divide-gray-200">
                  {currentDoors.map(door => (
                    <tr key={door._id} className="hover:bg-gray-50 transition-colors duration-200">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{door.id || ''}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{door.designName || ''}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{door.doorType?.parameterName || ''}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {door.doorType?.isThickness && door.thickness ? door.thickness.parameterName : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{door.size?.parameterName || ''}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {editingStock[door._id] ? (
                          <input
                            type="number"
                            min="0"
                            value={stockValues[door._id]?.stock || 0}
                            onChange={(e) => handleStockChange(door._id, 'stock', e.target.value)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        ) : (
                          door.stock || 0
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {editingStock[door._id] ? (
                          <input
                            type="number"
                            min="0"
                            value={stockValues[door._id]?.damaged_stock || 0}
                            onChange={(e) => handleStockChange(door._id, 'damaged_stock', e.target.value)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        ) : (
                          door.damaged_stock || 0
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {editingImage === door._id ? (
                          <div className="space-y-2">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                            />
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleSaveImage(door)}
                                className="bg-green-500 hover:bg-green-600 text-white py-1 px-2 rounded text-xs transition duration-200"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelImage}
                                className="bg-gray-500 hover:bg-gray-600 text-white py-1 px-2 rounded text-xs transition duration-200"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => showImageModal(door)}
                              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition duration-200"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleEditImage(door)}
                              className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg transition duration-200"
                            >
                              Edit
                            </button>
                          </div>
                        )}
                      </td>                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {editingStock[door._id] ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleSaveStock(door)}
                              className="bg-green-500 hover:bg-green-600 text-white py-2 px-3 rounded-lg transition duration-200"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => handleCancelStock(door)}
                              className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-3 rounded-lg transition duration-200"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex space-x-2">
                            <EditStockButton door={door} onEdit={handleEditStock} />
                            <DeleteDoorButton door={door} onDelete={handleDeleteDoor} />
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-b-lg">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">
                        {filteredDoors.length === 0 ? 0 : indexOfFirstDoor + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium">
                        {Math.min(indexOfLastDoor, filteredDoors.length)}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium">{filteredDoors.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Previous</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      {generatePageNumbers().map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Next</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}

            {filteredDoors.length === 0 && !isLoading && (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No doors found</h3>
                <p className="text-gray-500">Try adjusting your search terms or check back later.</p>
              </div>
            )}
          </>
        )}

        <Modal isOpen={isModalOpen} onClose={closeModal} door={selectedDoor} />
      </div>
    </div>
  );
}

export default DoorsStockStatus;